/**
 * Utility to compress an image File to stay under 100 KB (102,400 bytes)
 * while preserving maximum possible resolution and high visual quality.
 */

export interface CompressionResult {
  file: File
  originalSize: number
  compressedSize: number
  isCompressed: boolean
}

export async function compressImageToUnder100KB(
  file: File,
  maxSizeBytes: number = 100 * 1024
): Promise<CompressionResult> {
  const originalSize = file.size

  // Only compress images (skip PDFs or non-image files)
  const isImage = file.type.startsWith('image/') || /\.(jpg|jpeg|png|webp|bmp|gif|tiff)$/i.test(file.name)
  if (!isImage) {
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      isCompressed: false,
    }
  }

  // If the image is already under 100 KB, return as is
  if (originalSize <= maxSizeBytes) {
    return {
      file,
      originalSize,
      compressedSize: originalSize,
      isCompressed: false,
    }
  }

  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onerror = () => reject(new Error('ফাইল পড়তে সমস্যা হয়েছে'))

    reader.onload = (event) => {
      const img = new Image()
      img.onerror = () => reject(new Error('ছবি প্রসেস করতে সমস্যা হয়েছে'))

      img.onload = async () => {
        try {
          let width = img.naturalWidth || img.width
          let height = img.naturalHeight || img.height

          // Cap initial max dimension to avoid canvas memory limits (max 2560px for high-res)
          const MAX_INITIAL_DIM = 2560
          if (width > MAX_INITIAL_DIM || height > MAX_INITIAL_DIM) {
            const ratio = Math.min(MAX_INITIAL_DIM / width, MAX_INITIAL_DIM / height)
            width = Math.round(width * ratio)
            height = Math.round(height * ratio)
          }

          const canvas = document.createElement('canvas')
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            return resolve({
              file,
              originalSize,
              compressedSize: originalSize,
              isCompressed: false,
            })
          }

          // Test WebP support in browser
          let mimeType = 'image/webp'
          const testCanvas = document.createElement('canvas')
          testCanvas.width = 1
          testCanvas.height = 1
          if (testCanvas.toDataURL('image/webp').indexOf('data:image/webp') !== 0) {
            mimeType = 'image/jpeg'
          }

          let currentWidth = width
          let currentHeight = height
          let scaleFactor = 1.0
          let quality = 0.92
          let bestBlob: Blob | null = null

          // Iteratively adjust quality & resolution until size <= maxSizeBytes (100 KB)
          for (let attempt = 0; attempt < 15; attempt++) {
            canvas.width = Math.round(currentWidth)
            canvas.height = Math.round(currentHeight)

            ctx.clearRect(0, 0, canvas.width, canvas.height)
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height)

            const blob = await new Promise<Blob | null>((res) =>
              canvas.toBlob((b) => res(b), mimeType, quality)
            )

            if (blob) {
              bestBlob = blob
              if (blob.size <= maxSizeBytes) {
                // Successfully compressed under 100 KB!
                break
              }
            }

            // Reduce quality step by step down to 0.45
            if (quality > 0.45) {
              quality -= 0.10
            } else {
              // If quality is already reduced, scale down resolution by 12% per attempt
              scaleFactor *= 0.88
              currentWidth = width * scaleFactor
              currentHeight = height * scaleFactor
              // Boost quality slightly for lower resolution to maintain sharpness
              quality = Math.max(0.65, quality)
            }
          }

          if (bestBlob && bestBlob.size < originalSize) {
            const ext = mimeType === 'image/webp' ? 'webp' : 'jpg'
            const originalName = file.name.replace(/\.[^/.]+$/, '')
            const compressedFileName = `${originalName}.${ext}`

            const compressedFile = new File([bestBlob], compressedFileName, {
              type: mimeType,
              lastModified: Date.now(),
            })

            resolve({
              file: compressedFile,
              originalSize,
              compressedSize: compressedFile.size,
              isCompressed: true,
            })
          } else {
            resolve({
              file,
              originalSize,
              compressedSize: originalSize,
              isCompressed: false,
            })
          }
        } catch (err) {
          reject(err)
        }
      }

      img.src = event.target?.result as string
    }

    reader.readAsDataURL(file)
  })
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`
}
